import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { useRevenueCat } from "@/hooks/useRevenueCat";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type PricingTier = {
  id: string;
  name: string;
  price: string;
  period: string;
  originalPrice?: string;
  savings?: string;
  popular?: boolean;
  features: string[];
};

const pricingTiers: PricingTier[] = [
  {
    id: "$rc_monthly",
    name: "Monthly",
    price: "$0.99",
    period: "per month",
    features: ["Auto-translation in real-time", "Voice message translation", "Unlimited contacts", "Group chats", "3-day free trial"],
  },
  {
    id: "$rc_annual",
    name: "Yearly",
    price: "$9.99",
    period: "per year",
    originalPrice: "$11.88",
    savings: "Save 16%",
    popular: true,
    features: ["Everything in Monthly", "Save $1.88 per year", "Best value", "3-day free trial"],
  },
];

export const PricingSelection = () => {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<string>("$rc_annual");
  const [loading, setLoading] = useState(false);
  const {
    purchaseSubscription,
    offerings,
    isConfigured,
    isInitializing,
    initializationError,
    reinitialize,
  } = useRevenueCat();
  const { checkSubscription } = useAuth();
  const { toast } = useToast();

  const handleBack = async () => {
    // Sign the user out so they can leave the subscription page
    await supabase.auth.signOut();
    navigate("/");
  };

  const handlePurchase = async () => {
    setLoading(true);
    try {
      if (!offerings || !isConfigured) {
        toast({
          title: initializationError ? "In-app purchases unavailable" : "Please wait",
          description: initializationError
            ? "We could not load subscription plans. Please try again in a few minutes or check your internet connection."
            : "Subscription plans are still loading. Try again in a moment.",
          variant: "destructive",
        });
        return;
      }

      const success = await purchaseSubscription(selectedTier);

      if (success) {
        toast({
          title: "Success!",
          description: "Your subscription is now active.",
        });

        // Wait a moment for RevenueCat to sync, then check subscription
        setTimeout(async () => {
          await checkSubscription();
          // Give another moment for state to update before reload as fallback
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }, 1500);
      } else {
        // User cancelled - check Xcode logs for details
        console.log("[PricingSelection] Purchase returned false - check Xcode logs for details");
      }
    } catch (error: any) {
      console.error("Error purchasing subscription:", error);

      // Extract more detailed error message
      let errorMessage = "Failed to complete purchase. Please try again.";
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.underlyingErrorMessage) {
        errorMessage = error.underlyingErrorMessage;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      toast({
        title: "Purchase Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 w-full">
      <div className="space-y-3">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Choose your plan</h2>
        </div>
        <p className="text-center text-sm text-muted-foreground">
          All plans include a 3-day free trial
        </p>
      </div>

      <div className="grid gap-4">
        {pricingTiers.map((tier) => (
          <Card
            key={tier.id}
            className={cn(
              "relative cursor-pointer transition-all hover:shadow-lg",
              selectedTier === tier.id && "ring-2 ring-primary shadow-lg",
            )}
            onClick={() => setSelectedTier(tier.id)}
          >
            {tier.popular && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">Most Popular</Badge>
            )}
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold">{tier.name}</h3>
                    {tier.savings && (
                      <Badge variant="secondary" className="text-xs">
                        {tier.savings}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold">{tier.price}</span>
                    <span className="text-muted-foreground text-sm">{tier.period}</span>
                  </div>
                  {tier.originalPrice && (
                    <p className="text-sm text-muted-foreground line-through mb-2">{tier.originalPrice}/year</p>
                  )}
                  <ul className="space-y-2">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div
                  className={cn(
                    "h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                    selectedTier === tier.id ? "border-primary bg-primary" : "border-muted",
                  )}
                >
                  {selectedTier === tier.id && <Check className="h-4 w-4 text-primary-foreground" />}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button
        onClick={handlePurchase}
        disabled={loading || isInitializing || !offerings || !isConfigured}
        size="lg"
        className="w-full"
      >
        {(loading || isInitializing) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {initializationError
          ? "Plans unavailable"
          : offerings && isConfigured
            ? "Start Free Trial"
            : "Loading plans..."}
      </Button>

      {initializationError && (
        <div className="space-y-2">
          <p className="text-xs text-center text-destructive">
            {initializationError}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={async () => {
              setLoading(true);
              try {
                await reinitialize();
              } finally {
                setLoading(false);
              }
            }}
          >
            Try again
          </Button>
        </div>
      )}

      <p className="text-xs text-center text-muted-foreground">Cancel anytime. No charges during trial period.</p>

      <div className="text-xs text-center text-muted-foreground space-y-2 pt-2 border-t">
        <p>
          Subscriptions auto-renew unless cancelled at least 24 hours before the end of the current period. 
          Your account will be charged for renewal within 24 hours prior to the end of the current period.
        </p>
        <p>
          By subscribing, you agree to our{" "}
          <a
            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary"
          >
            Terms of Use (EULA)
          </a>{" "}
          and{" "}
          <a
            href="https://c16458328a2848af9115a825783212c2.lovableproject.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary"
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};
